import React, { useEffect, useState, useRef } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  CircularProgress,
  Box,
  useTheme
} from '@mui/material';
import {
  Gear,
  SignOut,
  Buildings,
  Check,
  Question
} from '@phosphor-icons/react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import { UserRole } from '@/types/userTypes';
import { fetchCompanyLogoBlobById } from '@/services/companyLogoService';

interface ProfileMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSettingsOpen: () => void;
  onLogout: () => void;
  onDrawerClose?: () => void; // Close parent drawer on mobile after navigation
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({
  anchorEl,
  onClose,
  onSettingsOpen,
  onLogout,
  onDrawerClose
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const {
    appUser,
    selectedUserRole,
    setSelectedRole,
    isLoading: isUserContextLoading
  } = useAppUser();

  // Store logo URLs for all companies (keyed by company ID)
  const [companyLogos, setCompanyLogos] = useState<Record<string, string>>({});
  const logoObjectUrlsRef = useRef<Record<string, string>>({});

  // Load logos for all companies when menu is open
  useEffect(() => {
    if (!anchorEl || !appUser?.id || !appUser.userRoles || appUser.userRoles.length === 0) return;

    const loadCompanyLogos = async () => {
      const newLogos: Record<string, string> = {};

      // Fetch logos for all companies the user has access to
      await Promise.all(
        appUser.userRoles.map(async (role) => {
          // Skip if we already have this logo cached
          if (logoObjectUrlsRef.current[role.company.id]) {
            newLogos[role.company.id] = logoObjectUrlsRef.current[role.company.id];
            return;
          }

          try {
            const blob = await fetchCompanyLogoBlobById(
              appUser.id,
              role.company.id,
              getAccessTokenSilently,
              getAccessTokenWithPopup,
              false
            );

            if (blob) {
              const url = URL.createObjectURL(blob);
              logoObjectUrlsRef.current[role.company.id] = url;
              newLogos[role.company.id] = url;
            }
          } catch (error) {
            console.error(`Failed to load logo for ${role.company.name}:`, error);
          }
        })
      );

      setCompanyLogos(newLogos);
    };

    loadCompanyLogos();

    // Cleanup function to revoke object URLs when component unmounts
    return () => {
      Object.values(logoObjectUrlsRef.current).forEach(url => {
        URL.revokeObjectURL(url);
      });
      logoObjectUrlsRef.current = {};
    };
  }, [anchorEl, appUser?.id, appUser?.userRoles, getAccessTokenSilently, getAccessTokenWithPopup]);

  const handleRoleSwitch = async (role: UserRole) => {
    // Avoid re-selecting the current role
    if (role.id === selectedUserRole?.id) {
      onClose();
      return;
    }

    try {
      await setSelectedRole(role);
    } catch (error) {
      console.error('Failed to switch role from ProfileMenu:', error);
    } finally {
      onClose();
    }
  };

  const canShowRoles = appUser && appUser.userRoles.length > 0;

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          minWidth: 240,
          maxWidth: 260,
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 8px 24px rgba(0, 0, 0, 0.6)' 
            : '0 8px 24px rgba(0, 0, 0, 0.12)',
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          mt: 1.5,
          overflow: 'hidden'
        }
      }}
    >
      {/* User Info Header */}
      {appUser && (
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {appUser.displayName || 'User'}
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              color: theme.palette.text.secondary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {appUser.email}
          </Typography>
        </Box>
      )}

      {/* Settings */}
      <MenuItem
        onClick={() => {
          onSettingsOpen();
          onClose();
        }}
        sx={{
          py: 1.5,
          px: 3,
          mt: 0.5,
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          <Gear size={18} color={theme.palette.text.secondary} />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{ 
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          {intl.formatMessage({
            id: 'sidebar.settings',
            defaultMessage: 'Settings'
          })}
        </ListItemText>
      </MenuItem>

      {/* Help */}
      <MenuItem
        onClick={() => {
          navigate('/help');
          onClose();
          onDrawerClose?.();
        }}
        sx={{
          py: 1.5,
          px: 3,
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          <Question size={18} color={theme.palette.text.secondary} />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{ 
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          Help
        </ListItemText>
      </MenuItem>

      {/* Organization */}
      <MenuItem
        onClick={() => {
          navigate('/organization');
          onClose();
          onDrawerClose?.();
        }}
        sx={{
          py: 1.5,
          px: 3,
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          <Buildings size={18} color={theme.palette.text.secondary} />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{ 
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          {intl.formatMessage({
            id: 'sidebar.organization',
            defaultMessage: 'Organization'
          })}
        </ListItemText>
      </MenuItem>

      {/* Role Switching Section */}
      {canShowRoles && appUser.userRoles.length > 1 && (
        <Divider sx={{ my: 1.5 }} />
      )}

      {canShowRoles && appUser.userRoles.length > 1 && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              color: theme.palette.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.8px'
            }}
          >
            {intl.formatMessage({
              id: 'profileMenu.switchOrganization',
              defaultMessage: 'Switch Organization'
            })}
          </Typography>
        </Box>
      )}

      {/* Loading indicator for roles */}
      {canShowRoles &&
        appUser.userRoles.length > 1 &&
        isUserContextLoading &&
        appUser.userRoles.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2
            }}
          >
            <CircularProgress size={20} />
          </Box>
        )}

      {/* List of available roles */}
      {canShowRoles &&
        appUser.userRoles.length > 1 &&
        appUser.userRoles.map((role) => {
          const isSelected = role.id === selectedUserRole?.id;
          return (
            <MenuItem
              key={role.id}
              onClick={() => handleRoleSwitch(role)}
              disabled={isUserContextLoading}
              sx={{
                py: 1.5,
                px: 3,
                my: 0.5,
                borderRadius: '6px',
                backgroundColor: isSelected
                  ? theme.palette.mode === 'dark'
                    ? 'rgba(59, 130, 246, 0.15)'
                    : 'rgba(59, 130, 246, 0.08)'
                  : 'transparent',
                '&:hover': {
                  backgroundColor: isSelected
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(59, 130, 246, 0.2)'
                      : 'rgba(59, 130, 246, 0.12)'
                    : theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : '#f3f4f6'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {companyLogos[role.company.id] ? (
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '4px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                    }}
                  >
                    <img
                      src={companyLogos[role.company.id]}
                      alt={role.company.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                ) : (
                  <Buildings
                    size={18}
                    color={theme.palette.text.secondary}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{
                  fontSize: '14px',
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected
                    ? theme.palette.primary.main
                    : theme.palette.text.primary
                }}
              >
                {role.company.name}
              </ListItemText>
              {isSelected && (
                <Check
                  size={18}
                  color={theme.palette.primary.main}
                  style={{ marginLeft: 'auto' }}
                />
              )}
            </MenuItem>
          );
        })}

      <Divider sx={{ my: 1.5 }} />

      {/* Logout */}
      <MenuItem
        onClick={() => {
          onLogout();
          onClose();
        }}
        sx={{
          py: 1.5,
          px: 3,
          mb: 0.5,
          color: 'error.main',
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)'
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
          <SignOut size={18} color={theme.palette.error.main} />
        </ListItemIcon>
        <ListItemText
          primaryTypographyProps={{ 
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          {intl.formatMessage({
            id: 'sidebar.logout',
            defaultMessage: 'Logout'
          })}
        </ListItemText>
      </MenuItem>
    </Menu>
  );
};
